import { useEffect, useState, useMemo } from 'react'
import { BrowserRouter, Route } from 'react-router-dom'
import { Admin, CustomRoutes, Resource } from 'react-admin'
import { ForgotPasswordPage } from 'ra-supabase'

import { CircularProgress } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { fr } from 'date-fns/locale'

import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import { ServiceWorkerProvider, useServiceWorker } from './contexts/ServiceWorkerContext'

import theme from './common/theme'
import LoginPage from './LoginPage'
import { MyLayout }  from './Layout'
import darkTheme from './common/darkTheme'
import { i18nProvider } from './i18nProvider'
import LoadingPage from './Design/LoadingPage'
import SetPasswordPage from './SetPasswordPage'
import ResetPasswordPage from './ResetPasswordPage'
import DispoPublicPage from './events/DispoPublicPage'
import combinedDataProvider from './supabase/dataProvider'
import { ProfileProvider } from './contexts/ProfileContext'
import customAuthProvider from './supabase/customAuthProvider'

import Dashboard from './dashboards/'

const ReactAdminApp = () => {
  const { setIsUpdateAvailable, setUpdateAssets } = useServiceWorker()
  const authProvider = useMemo(() => customAuthProvider(), [])

  // State for dynamically loaded resources
  const [resources, setResources] = useState([])

  useEffect(() => {
    // Dynamically import resources
    Promise.all([
      import('./leads').then((mod) => ({ name: 'leads', module: mod.default })),
      import('./events').then((mod) => ({ name: 'events', module: mod.default })),
      import('./inboundContacts').then((mod) => ({ name: 'inbound_contacts', module: mod.default })),
      import('./tasks').then((mod) => ({ name: 'tasks', module: mod.default })),
      import('./tags').then((mod) => ({ name: 'tags', module: mod.default })),
      import('./conversations').then((mod) => ({ name: 'conversations', module: mod.default })),
      import('./profiles').then((mod) => ({ name: 'profiles', module: mod.default })),
      import('./centers').then((mod) => ({ name: 'centers', module: mod.default })),
      import('./eventTypes').then((mod) => ({ name: 'types', module: mod.default })),
      import('./schools').then((mod) => ({ name: 'schools', module: mod.default })),
      import('./facs').then((mod) => ({ name: 'facultes', module: mod.default })),
      import('./leadYears').then((mod) => ({ name: 'lead_years', module: mod.default })),
      import('./leadSources').then((mod) => ({ name: 'lead_sources', module: mod.default })),
      import('./status_inscription').then((mod) => ({ name: 'status_inscription', module: mod.default })),
      import('./rendezvous').then((mod) => ({ name: 'rendezvous', module: mod.default })),
      import('./parcoursup').then((mod) => ({ name: 'parcoursup', module: mod.default })),
      process.env.REACT_APP_SUPABASE_URL?.includes('hrfrmccmvoqcsrizaemh')
        ? import('./importTracking').then((mod) => ({ name: 'conversation_import_health', module: mod.default }))
        : null,
    ].filter(Boolean)).then(setResources)
  }, [])

  useEffect(() => {
    serviceWorkerRegistration.register({
      onUpdate: (registration) => {
        setIsUpdateAvailable(true)

        const handleUpdate = async () => {
          if (registration && registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' })
          }

          serviceWorkerRegistration.unregister()

          const cacheNames = await caches.keys()
          await Promise.all(cacheNames.map(name => caches.delete(name)))

          window.location.reload()
        }

        setUpdateAssets(handleUpdate)
      },
    })
  }, [setIsUpdateAvailable, setUpdateAssets])

  useEffect(() => {
    // Force check when user focuses the tab
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        navigator.serviceWorker.getRegistration().then(reg => {
          if (reg) {
            reg.update()
          }
        })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])
  
  // pre-loader mimic before <LoadingPage /> can run
  if (resources.length === 0) return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
        textAlign: 'center',
        fontSize: '1.25rem',
      }}>
        <h1></h1>
      
        <CircularProgress />

       <span style={{ marginTop: '40px', fontSize: '1rem' }}></span>
      </div>
    </div>
  )

  return (
    <Admin
      requireAuth
      loading={LoadingPage}
      dataProvider={combinedDataProvider}
      authProvider={authProvider}
      i18nProvider={i18nProvider}
      loginPage={LoginPage}
      layout={MyLayout}
      theme={theme}
      darkTheme={darkTheme}
      dashboard={Dashboard}
    >
      <CustomRoutes noLayout>
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path={ForgotPasswordPage.path} element={<ForgotPasswordPage />} />
        <Route path="/dispo/:token" element={<DispoPublicPage />} />
      </CustomRoutes>

      {/* Render Resources Dynamically */}
      {resources.map(({ name, module }) => (
        <Resource
          key={name}
          name={name}
          {...module}
          recordRepresentation={(record) => record?.name || `${record?.first_name} ${record?.last_name}`}
        />
      ))}
    </Admin>
  )
}

const App = () => (
  <ServiceWorkerProvider>
    <ProfileProvider>
      <BrowserRouter>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
          <ReactAdminApp />
        </LocalizationProvider>
      </BrowserRouter>
    </ProfileProvider>
  </ServiceWorkerProvider>
)

export default App